import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Snackbar, TextField, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingDetails, bookingService, CancelBookingRequest, ExtendStayRequest } from '../../services/api/bookingService';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { LocalizationProvider, DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import api from '../../services/api/axiosConfig';


const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#0DA85A',
    }),
  },
}));
interface ModifyBookingContentProps {
  bookingId: string;
}

const ModifyBookingContent = ({ bookingId }: ModifyBookingContentProps) => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<'4hours' | '8hours' | 'fullday' | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [value, setValue] = useState<Date | null>(null);
  const [calenderOpen, setCalenderOpen] = useState(false);
  const [checkOutValue, setCheckOutValue] = useState<Date | null>(null);
  const [checkOutCalenderOpen, setCheckOutCalenderOpen] = useState(false);
  const [time, setTime] = useState<Date | null>(new Date());  // Current time
  const [openTime, setOpenTime] = useState(false);
  const [timelineProgress, setTimelineProgress] = useState(0);
  const [bookingPin, setBookingPin] = useState<string | null>(null);
  const [botelName, setBotelName] = useState<string>('');

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  // Fetch booking PIN
  useEffect(() => {
    const fetchBookingPin = async () => {
      if (!booking || !booking.id || booking.status === "cancelled" || booking.status === "checked out" || !booking.identity_verified) {
        return;
      }

      // Check if checkout time has expired
      if (booking.check_out) {
        const now = new Date();
        const checkOut = new Date(booking.check_out);
        if (now > checkOut) {
          // Checkout time has expired, don't fetch PIN
          return;
        }
      }

      try {
        const response = await api.get(`/verification/booking/${booking.id}/pin`);
        
        if (response.data?.pin) {
          setBookingPin(response.data.pin);
        } else {
          setBookingPin(null);
        }
      } catch (error: any) {
        console.error("Error fetching booking PIN:", error);
        setBookingPin(null);
      }
    };

    if (booking) {
      fetchBookingPin();
    }
  }, [booking]);

  // Fetch botel details
  useEffect(() => {
    const fetchBotelDetails = async () => {
      if (!booking?.botel_id) {
        return;
      }

      try {
        const response = await api.get(`/botels/${booking.botel_id}`);
        if (response.data?.name) {
          setBotelName(response.data.name);
        }
      } catch (error: any) {
        console.error("Error fetching botel details:", error);
        // Fallback to booking.botel?.name if available
        if (booking.botel?.name) {
          setBotelName(booking.botel.name);
        }
      }
    };

    if (booking) {
      fetchBotelDetails();
    }
  }, [booking?.botel_id]);

  const calculateTimelineProgress = () => {
    if (!booking || !booking.check_in || !booking.check_out) {
      setTimelineProgress(0);
      return;
    }
    
    const now = new Date();
    const checkIn = new Date(booking.check_in);
    const checkOut = new Date(booking.check_out);
    
    // If current time is before check-in, progress is 0%
    if (now < checkIn) {
      setTimelineProgress(0);
      return;
    }
    
    // If current time is after check-out, progress is 100%
    if (now > checkOut) {
      setTimelineProgress(100);
      return;
    }
    
    // Calculate progress percentage
    const totalDuration = checkOut.getTime() - checkIn.getTime();
    const elapsed = now.getTime() - checkIn.getTime();
    const progress = (elapsed / totalDuration) * 100;
    
    setTimelineProgress(Math.min(100, Math.max(0, progress)));
  };

  // Update date values and calculate progress when booking is loaded
  useEffect(() => {
    if (booking) {
      // Set check-in date
      if (booking.check_in) {
        setValue(new Date(booking.check_in));
      }
      
      // Set check-out date
      if (booking.check_out) {
        setCheckOutValue(new Date(booking.check_out));
      }
      
      // Update current time
      setTime(new Date());
      
      // Calculate timeline progress
      if (booking.check_in && booking.check_out) {
        const now = new Date();
        const checkIn = new Date(booking.check_in);
        const checkOut = new Date(booking.check_out);
        
        // If current time is before check-in, progress is 0%
        if (now < checkIn) {
          setTimelineProgress(0);
        }
        // If current time is after check-out, progress is 100%
        else if (now > checkOut) {
          setTimelineProgress(100);
        }
        // Calculate progress percentage
        else {
          const totalDuration = checkOut.getTime() - checkIn.getTime();
          const elapsed = now.getTime() - checkIn.getTime();
          const progress = (elapsed / totalDuration) * 100;
          setTimelineProgress(Math.min(100, Math.max(0, progress)));
        }
      } else {
        setTimelineProgress(0);
      }
    }
  }, [booking]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bookingService.getBookingById(bookingId);
      setBooking(data);
    } catch (err) {
      console.error('Failed to fetch booking details:', err);
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTimeRemaining = () => {
    if (!booking || !booking.check_out) return { hours: 0, minutes: 0 };
    
    const now = new Date();
    const checkOut = new Date(booking.check_out);
    const diffMs = checkOut.getTime() - now.getTime();
    
    if (diffMs <= 0) return { hours: 0, minutes: 0 };
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return { hours, minutes };
  };

  const getExtensionPrice = (type: '4hours' | '8hours' | 'fullday') => {
    switch (type) {
      case '4hours': return 20.00;
      case '8hours': return 35.00;
      case 'fullday': return 50.00;
      default: return 0;
    }
  };

  const handleExtendStay = async () => {
    if (!selectedExtension || !booking) return;
    
    try {
      setProcessing(true);
      
      const extensionPrice = getExtensionPrice(selectedExtension);
      
      // Calculate extended hours
      const extensionHours = selectedExtension === '4hours' ? 4 : selectedExtension === '8hours' ? 8 : 24;
      
      // Save booking details and extension info to session storage
      const extensionData = {
        bookingId: bookingId,
        booking: booking,
        extensionType: selectedExtension,
        extensionHours: extensionHours,
        extensionAmount: extensionPrice,
        isExtension: true
      };
      
      sessionStorage.setItem('extensionData', JSON.stringify(extensionData));
      
      // Save booking details for payment page (similar to new booking flow)
      const bookingDetails = {
        bookingId: bookingId,
        baseRate: extensionPrice,
        insurancePrice: 0,
        overstayPrice: 0,
        taxesAndFees: 0,
        total: extensionPrice,
        isExtension: true,
        extensionType: selectedExtension,
        extensionHours: extensionHours,
        originalBooking: booking
      };
      
      sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
      
      setExtendDialogOpen(false);
      setSelectedExtension(null);
      setProcessing(false);
      
      // Redirect to payment details page
      navigate('/payment-details');
    } catch (err) {
      console.error('Failed to extend stay:', err);
      setSnackbar({
        open: true,
        message: 'Failed to extend stay. Please try again.',
        severity: 'error'
      });
      setProcessing(false);
    }
  };

  const handleCancelBooking = async () => {
    try {
      setProcessing(true);
      const request: CancelBookingRequest = {
        bookingId,
        reason: cancelReason
      };
      
      const updatedBooking = await bookingService.cancelBooking(request);
      setBooking(updatedBooking);
      setCancelDialogOpen(false);
      setCancelReason('');
      setSnackbar({
        open: true,
        message: 'Booking cancelled successfully!',
        severity: 'success'
      });
      
      // Navigate back to dashboard after a short delay
      setTimeout(() => {
        navigate('/client/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      setSnackbar({
        open: true,
        message: 'Failed to cancel booking. Please try again.',
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleAddInsurance = async () => {
    try {
      setProcessing(true);
      const updatedBooking = await bookingService.addInsurance(bookingId);
      setBooking(updatedBooking);
      setSnackbar({
        open: true,
        message: 'Insurance added successfully!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to add insurance:', err);
      setSnackbar({
        open: true,
        message: 'Failed to add insurance. Please try again.',
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };
  if (loading) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 1, sm: 2, md: 3 }, 
        background: '#151A23', 
        minHeight: '100vh', 
        color: '#fff', 
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress sx={{ color: '#90caf9' }} />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 1, sm: 2, md: 3 }, 
        background: '#151A23', 
        minHeight: '100vh', 
        color: '#fff', 
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error || 'Booking not found'}</Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/client/dashboard')}
            sx={{ borderRadius: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  const { hours, minutes } = calculateTimeRemaining();
  const timeRemaining = hours > 0 || minutes > 0;

  return (
    <>
    <Box sx={{ background: '#151A23', color: '#fff', marginTop: "64px", mb:"-80px", borderTop:"1px solid #2C353F", borderBottom:"1px solid #2C353F", padding: "17px 31px",
      "& svg":{
        fontSize: "24px",
        display: "block",
      }
     }}>
        <Typography sx={{ fontSize: 18, fontWeight: "semibold", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Stay Details
        </Typography>
        </Box>
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, background: '#151A23', minHeight: '100vh', color: '#fff', marginTop: 10 }}>
      {/* Header */}
      
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }} elevation={4}>
        <Box>
      <Typography variant="h6" fontWeight="bold" fontSize={28}>{botelName || booking?.botel?.name || 'N/A'}</Typography>
          <Typography variant="caption" sx={{ color: "white", fontSize: 16 }}>Booking #{booking?.id}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ borderRadius: 2, fontSize: 16 }}
            onClick={handleAddInsurance}
            disabled={processing || booking.payment_status === 'completed'}
          >
            {processing ? <CircularProgress size={20} /> : 'Add Insurance'}
          </Button>
          <Button variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
          <img
                  src="/icons/pdf-icon.svg"
                  alt="calendar icon"
                  style={{ width: 20, height: 20, marginRight: 10}}
                />
            View Invoice
            </Button>
        </Box>
      </Paper>

      {/* Booking Timeline */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3 }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography fontWeight="bold" fontSize={24}>Booking Timeline</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* <Button variant="contained" size="small" sx={{fontSize: 16, background: '#181F2A', color: 'white', fontWeight: 700, border:"1px solid white", borderRadius: 2, textTransform: 'none', px: 2, mr: 1, '&:hover': { background: '#232B3E' } }}>550 Points Until Gold</Button> */}
            {/* <IconButton size="small" sx={{ p:"11px 15px", color: 'white', background: '#2156C4', borderRadius: 2, "&:hover": { opacity: 0.5 } }}><Typography fontSize={16} sx={{mr:"10px", fontWeight: 700}}>Edit</Typography><EditIcon sx={{fontSize: 20}} /></IconButton> */}
          </Box>
        </Box>
        <Box sx={{ width: '100%', mb: 3, mt: 3}}>
          <BorderLinearProgress variant="determinate" value={timelineProgress} />
        </Box>
        <Grid container spacing={2}>
          <Grid size={{xs: 12, md:4}} sx={{ background: '#131A21', borderRadius: 2, p: 4, pb: 2, "& button.MuiIconButton-root" :{
          display: "none",
        }, "& .MuiInputBase-root":{
          p:0,
        } }}>
            <Typography fontSize={16} >Check-In</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
  value={value}
  onChange={(newValue) => setValue(newValue)}
  open={false}
  onOpen={() => setCalenderOpen(true)}
  onClose={() => setCalenderOpen(false)}
  enableAccessibleFieldDOMStructure={false}
  slotProps={{
    textField: {
      fullWidth: true,
      onClick: () => setCalenderOpen(true),
      InputLabelProps: { shrink: true },
      sx: {
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            border: "none", // remove border
          },
          "&:hover fieldset": {
            border: "none",
          },
          "&.Mui-focused fieldset": {
            border: "none",
          },
        },
      },
      slotProps: {
        input: {
          startAdornment: (
            <span
              style={{ cursor: "pointer", paddingLeft: 0 }}
              onClick={() => setCalenderOpen(true)}
            >
              <img
                src="/icons/calender-icon.svg"
                alt="calender"
                style={{ paddingRight: "10px", display: "block" }}
              />
            </span>
          ),
        },
      },
    },
  }}
/>

    </LocalizationProvider>
                {/* <Typography variant="body2">{formatDate(booking.check_in)}</Typography> */}
          </Grid>
          <Grid size={{xs: 12, md:4}} sx={{ background: '#131A21', borderRadius: 2, p: 4, pb: 2, "& button.MuiIconButton-root" :{
          display: "none",
        }, "& .MuiInputBase-root":{
          p:0,
        } }}>
            <Typography fontSize={16} sx={{color:"#0DA85A"}}>Current Time</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimePicker
  value={time}
  onChange={(newValue) => setTime(newValue)}
  open={false}
  onOpen={() => setOpenTime(true)}
  onClose={() => setOpenTime(false)}
  enableAccessibleFieldDOMStructure={false}
  slotProps={{
    textField: {
      fullWidth: true,
      onClick: () => setOpenTime(true),
      sx: {
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            border: "none", // remove border
          },
          "&:hover fieldset": {
            border: "none",
          },
          "&.Mui-focused fieldset": {
            border: "none",
          },
        },
      },
      slotProps: {
        input: {
          startAdornment: (
            <span
              style={{ cursor: "pointer", paddingLeft: 0 }}
              onClick={() => setOpenTime(true)}
            >
              <img
                src="/icons/clock-icon.svg"
                alt="clock"
                style={{ paddingRight: "10px", display: "block" }}
              />
            </span>
          ),
        },
      },
    },
  }}
/>

    </LocalizationProvider>
                {/* <Typography variant="body2">{formatDate(booking.check_in)}</Typography> */}
          </Grid>
          <Grid size={{xs: 12, md:4}} sx={{ background: '#131A21', borderRadius: 2, p: 4, pb: 2, "& button.MuiIconButton-root" :{
          display: "none",
        }, "& .MuiInputBase-root":{
          p:0,
        } }}>
            <Typography fontSize={16} >Check-Out</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
  value={checkOutValue}
  onChange={(newValue) => setCheckOutValue(newValue)}
  open={false}
  onOpen={() => setCheckOutCalenderOpen(true)}
  onClose={() => setCheckOutCalenderOpen(false)}
  enableAccessibleFieldDOMStructure={false}
  slotProps={{
    textField: {
      fullWidth: true,
      onClick: () => setCheckOutCalenderOpen(true),
      InputLabelProps: { shrink: true },
      sx: {
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            border: "none", // remove border
          },
          "&:hover fieldset": {
            border: "none",
          },
          "&.Mui-focused fieldset": {
            border: "none",
          },
        },
      },
      slotProps: {
        input: {
          startAdornment: (
            <span
              style={{ cursor: "pointer", paddingLeft: 0 }}
              onClick={() => setCheckOutCalenderOpen(true)}
            >
              <img
                src="/icons/calender-icon.svg"
                alt="calender"
                style={{ paddingRight: "10px", display: "block" }}
              />
            </span>
          ),
        },
      },
    },
  }}
/>

    </LocalizationProvider>
                {/* <Typography variant="body2">{formatDate(booking.check_in)}</Typography> */}
          </Grid>
        </Grid>
       
      </Paper>
      <Box sx={{ mt: "-5px", mb: 2 }}>
          <Alert 
            icon={<AccessTimeOutlinedIcon sx={{ fontSize: 36, color: booking.status === 'cancelled' ? '#ef4444' : booking.status === 'checked out' ? '#0DA85A' : timeRemaining ? '#0DA85A' : '#8b4513' }} />} 
            severity={booking.status === 'cancelled' ? "error" : booking.status === 'checked out' ? "success" : timeRemaining ? "success" : "warning"} 
            sx={{ background: booking.status === 'cancelled' ? '#ef44441a' : booking.status === 'checked out' ? '#0da85a1a' : timeRemaining ? '#0da85a1a' : '#8b45132b', border: booking.status === 'cancelled' ? "1px solid #ef4444" : booking.status === 'checked out' ? "1px solid #0DA85A" : timeRemaining ? "1px solid #0DA85A" : "1px solid #8b4513", color: '#fff', borderRadius: 2, alignItems: 'center', fontWeight: 500, p: 2, display: 'flex' }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ color: "white", fontWeight: 500, fontSize: 18 }}>
                {booking.status === 'cancelled' 
                  ? 'Booking Cancelled' 
                  : booking.status === 'checked out'
                    ? 'Checked Out'
                    : timeRemaining 
                      ? 'You Have Plenty Of Time' 
                      : 'Stay Time Expired'}
              </Typography>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 24 }}>
                {booking.status === 'cancelled'
                  ? 'This booking has been cancelled. Please contact support if you have any questions.'
                  : booking.status === 'checked out'
                    ? 'You have successfully checked out. Thank you for using our service!'
                    : timeRemaining 
                      ? `${hours} Hours And ${minutes} Minutes Remaining In Your Stay`
                      : 'Your stay time has expired. Please check out or extend your stay.'}
              </Typography>
            </Box>
          </Alert>
        </Box>
      {/* Modify Your Stay Options */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3 }} elevation={4}>
        <Typography fontWeight="bold" fontSize={30} sx={{ mb: 2 }}>Modify Your Stay</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper onClick={() => {
                  if (booking.status === 'cancelled' || booking.status === 'checked out') return;
                  setSelectedExtension('4hours');
                  setExtendDialogOpen(true);
                }} sx={{ p: 2, py: "40px", background: '#131A21', borderRadius: "20px", textAlign: 'center', cursor: (booking.status === 'cancelled' || booking.status === 'checked out') ? 'not-allowed' : 'pointer', opacity: (booking.status === 'cancelled' || booking.status === 'checked out') ? 0.5 : 1, "&:hover": { opacity: (booking.status === 'cancelled' || booking.status === 'checked out') ? 0.5 : 0.5 } }} elevation={2}>
            <LocationOnOutlinedIcon sx={{ color: 'white', fontSize: 40 }} />
              <Typography variant="h6" sx={{ fontSize: "18px", pt: "10px"}}>Extend +4 Hours</Typography>
              <Typography variant="body2" sx={{ color: "white", fontSize: "16px", pt: "10px" }}>${getExtensionPrice('4hours').toFixed(2)}</Typography>
       
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper onClick={() => {
                  if (booking.status === 'cancelled' || booking.status === 'checked out') return;
                  setSelectedExtension('8hours');
                  setExtendDialogOpen(true);
                }} sx={{ p: 2, py: "40px", background: '#131A21', borderRadius: "20px", textAlign: 'center', cursor: (booking.status === 'cancelled' || booking.status === 'checked out') ? 'not-allowed' : 'pointer', opacity: (booking.status === 'cancelled' || booking.status === 'checked out') ? 0.5 : 1, "&:hover": { opacity: (booking.status === 'cancelled' || booking.status === 'checked out') ? 0.5 : 0.5 } }} elevation={2}>
            
            <img
                  src="/icons/say-goodbey.svg"
                  alt="calendar icon"
                  style={{ width: 40, height: 40, marginRight: 10}}
          />
              <Typography variant="h6" sx={{ fontSize: "18px", pt: "10px"}}>Extend +8 Hours</Typography>
              <Typography variant="body2" sx={{ color: "white", fontSize: "16px", pt: "10px" }}>${getExtensionPrice('8hours').toFixed(2)}</Typography>
       
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper onClick={() => {
                  if (booking.status === 'cancelled' || booking.status === 'checked out') return;
                  setSelectedExtension('fullday');
                  setExtendDialogOpen(true);
                }} sx={{ p: 2, py: "40px", background: '#131A21', borderRadius: "20px", textAlign: 'center', cursor: (booking.status === 'cancelled' || booking.status === 'checked out') ? 'not-allowed' : 'pointer', opacity: (booking.status === 'cancelled' || booking.status === 'checked out') ? 0.5 : 1, "&:hover": { opacity: (booking.status === 'cancelled' || booking.status === 'checked out') ? 0.5 : 0.5 } }} elevation={2}>
            <img
                  src="/icons/clock-icon.svg"
                  alt="calendar icon"
                  style={{ width: 40, height: 40, marginRight: 10}}
          />
              <Typography variant="h6" sx={{ fontSize: "18px", pt: "10px"}}>Full Day Extension</Typography>
              <Typography variant="body2" sx={{ color: "white", fontSize: "16px", pt: "10px" }}>
              ${getExtensionPrice('fullday').toFixed(2)}
              </Typography>
       
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Modify & Cancel Options */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, background: '#1F262C', borderRadius: 3 }} elevation={4}>
            <Typography fontWeight="bold" fontSize={30} sx={{ mb: 1 }}>Other Information</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: '#131A21', borderRadius: "20px", p: 4 }}>
            <Typography variant="body2" sx={{ color: 'white' }}>Total Amount: <b style={{ float: 'right' }}>${Number(booking.total_amount || 0).toFixed(2)}</b></Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>Botel Access Code: <b style={{ float: 'right' }}>{bookingPin ? `#${bookingPin}` : 'PIN is not generated'}</b></Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>Guest Email: <b style={{ float: 'right' }}>{booking.guest?.email}</b></Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>Booking Status: <b style={{ float: 'right' }}>{booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'N/A'}</b></Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2,pb:"65px", background: '#1F262C', borderRadius: 3 }} elevation={4}>
            <Typography fontWeight="bold" sx={{ mb: 1, fontSize: 30 }}>Cancellation Options</Typography>
            <Typography variant="body2" sx={{ color: "white", mb: "40px", mt: "30px" }}>Cancellation is Available With Partial Refund Before Check-Out Time</Typography>
            <Button 
              variant="contained" 
              color="error" 
              sx={{ borderRadius: 2, width: '100%' }}
              onClick={() => setCancelDialogOpen(true)}
              disabled={processing || booking.status === 'cancelled' || booking.status === 'checked out' || (booking.check_out ? new Date() > new Date(booking.check_out) : false)}
            >
              <CancelOutlinedIcon sx={{ color: 'white', fontSize: 20, mr: 1 }} />
              Cancel Booking
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Extend Stay Dialog */}
      <Dialog sx={{ "& .MuiPaper-root": { borderRadius: "20px", backgroundColor: "#1F262C" } }} open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Your Stay</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to extend your stay by {selectedExtension === '4hours' ? '4 hours' : selectedExtension === '8hours' ? '8 hours' : 'a full day'}?
          </Typography>
          <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
            Additional cost: ${selectedExtension ? getExtensionPrice(selectedExtension).toFixed(2) : '0.00'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={{ borderRadius: 2, color: 'white', borderColor: 'white' }} onClick={() => setExtendDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button 
            onClick={handleExtendStay} 
            variant="contained" 
            disabled={processing}
            sx={{ borderRadius: 2 }}
          >
            {processing ? <CircularProgress size={20} /> : 'Confirm Extension'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog
        sx={{ "& .MuiPaper-root": { borderRadius: "20px", backgroundColor: "#1F262C" } }}
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        aria-labelledby="cancel-booking-dialog-title"
        aria-describedby="cancel-booking-dialog-description"
      >
        <DialogTitle id="cancel-booking-dialog-title">
          Cancel Booking
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label="Reason for cancellation (optional)"
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={{ borderRadius: 2, color: 'white', borderColor: 'white' }} onClick={() => setCancelDialogOpen(false)} disabled={processing}>
            Keep Booking
          </Button>
          <Button 
            onClick={handleCancelBooking} 
            color="error" 
            variant="contained"
            disabled={processing}
            sx={{ borderRadius: 2 }}
          >
            {processing ? <CircularProgress size={20} /> : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
};

export default ModifyBookingContent; 